import { useState, useEffect } from "react";
import { toast } from "sonner";
import { g, p, eur, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HandCoins, Check } from "lucide-react";

/** Passa l'incasso a un collega: resta in sospeso finché lui non conferma "li ho io". */
export const PassaIncasso = ({ order, reload }) => {
  const { user } = useApp();
  const { t } = useI18n();
  const [staff, setStaff] = useState([]);
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");

  useEffect(() => {
    g("/staff").then((s) => setStaff(s.filter((x) => x.name !== user.name && x.active !== false)));
  }, [user.name]);

  useEffect(() => {
    setAmount(String(order?.remaining ?? ""));
  }, [order?.remaining]);

  const pending = order?.pending_payments || [];

  const send = async () => {
    const amt = Number(amount);
    if (!to) return toast.error(t("x.hand.pick"));
    if (!amt || amt <= 0) return toast.error(t("b.badAmount"));
    try {
      await p(`/orders/${order.id}/payments`, { method: "contanti", amount: amt, by: user.name, to });
      toast.success(t("x.hand.sent"));
      setTo("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const confirm = async (pid) => {
    try {
      await p(`/orders/${order.id}/payments/${pid}/confirm`, { by: user.name });
      toast.success(t("x.cash.confirmed"));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid="handover-panel">
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
        <HandCoins className="h-3.5 w-3.5" /> {t("x.hand.title")}
      </p>

      {pending.map((x) => (
        <div key={x.id} className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md bg-amber-50 p-2.5" data-testid={`conto-pending-${x.id}`}>
          <span className="text-xs text-amber-900">
            <span className="font-num font-bold">{eur(x.amount)}</span> · {t("x.hand.pending", { name: x.to })}
          </span>
          {x.to === user.name && (
            <Button size="sm" data-testid={`conto-confirm-${x.id}`} onClick={() => confirm(x.id)}>
              <Check className="mr-1 h-3.5 w-3.5" /> {t("x.cash.confirm")}
            </Button>
          )}
        </div>
      ))}

      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="min-w-[9rem] flex-1">
          <Label className="text-[11px]">{t("x.hand.to")}</Label>
          <select
            className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="handover-to-select"
          >
            <option value="">{t("x.hand.pick")}</option>
            {staff.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name} · {s.role_label || s.role}
              </option>
            ))}
          </select>
        </div>
        <Input className="h-10 w-28" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="handover-amount-input" />
        <Button className="h-10" onClick={send} data-testid="handover-send-btn">
          {t("x.hand.send")}
        </Button>
      </div>
    </div>
  );
};
